<template>
  <main class="edit-ticket-content">
    <header class="edit-ticket-content__badges">
      <BadgeSquare
        v-for="(badge, index) in badges"
        :key="index"
        :badgeText="badge.text"
        :customColor="getCustomColor(badge.type)"
        :grey="badge.type === 'place'"
        size="small"
      />
    </header>
    <nav class="edit-ticket-content__navigation">
      <UnderlineTabGroup :buttons="buttons" :selectedName="selectedTab" />
      <p>{{ t('general__data__ticket-number') }}: {{ ticketNumber ?? '-' }}</p>
    </nav>
    <EditTicketContentOverview v-show="selectedTab === 'overview'" />
    <EditTicketContentLog v-if="selectedTab === 'log'" />
    <EditTicketContentAttachments v-if="selectedTab === 'attachments'" />
  </main>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'

import { getTicketType } from '@/services/tickets.service'
import {
  getPriorityColorLight,
  getStatusColorLight,
  translatedPriority,
  translatedStatus
} from '@/utils/ticketHelpers'

import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import EditTicketContentAttachments from '@/components/features/tickets/EditTicketContentAttachments.vue'
import EditTicketContentLog from '@/components/features/tickets/EditTicketContentLog.vue'
import EditTicketContentOverview from '@/components/features/tickets/EditTicketContentOverview.vue'
import UnderlineTabGroup from '@/design-system/molecules/UnderlineTabGroup.vue'

interface EMITS {
  (e: 'selectedTab', val: string)
}
const emits = defineEmits<EMITS>()

type BadgeObject = {
  text: string
  type: string
}

const { locale, t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

type Props = {
  ticketNumber?: string
}

defineProps<Props>()

const buttons = computed(() => {
  const buttons = [
    {
      text: t('general__text__overview'),
      name: 'overview',
      onClick: () => {
        selectedTab.value = 'overview'
        emits('selectedTab', 'overview')
      }
    },
    {
      text: t('general__text__log'),
      name: 'log',
      onClick: () => {
        selectedTab.value = 'log'
        emits('selectedTab', 'log')
      }
    }
  ]
  if (store.getters.hasFileReaderAccess || store.getters.hasLinkReaderAccess)
    buttons.push({
      text: t('general__text__attachments'),
      name: 'attachments',
      onClick: () => {
        selectedTab.value = 'attachments'
        emits('selectedTab', 'attachments')
      }
    })
  return buttons
})

const badgeTypeText = ref<string>('')
const ticketParent = ref<string | undefined>()
const selectedTab = ref('overview')

const badges = computed<BadgeObject[]>(() => {
  if (!selectedTicket.value) return []
  const badgesArray: BadgeObject[] = []
  if (selectedTicket.value.priority) {
    badgesArray.push({
      text: translatedPriority(selectedTicket.value.priority),
      type: 'priority'
    })
  }
  if (badgeTypeText.value.length) {
    badgesArray.push({
      text: badgeTypeText.value,
      type: 'type'
    })
  }
  if (selectedTicket.value.status) {
    badgesArray.push({
      text: translatedStatus(selectedTicket.value.status),
      type: 'status'
    })
  }
  if (ticketParent.value)
    badgesArray.push({
      text: ticketParent.value,
      type: 'place'
    })
  return badgesArray
})
const selectedTicket = computed(() => piniaStore.selectedTicket)

function getCustomColor(badgeType: string | undefined) {
  switch (badgeType) {
    case 'priority':
      if (!selectedTicket.value?.priority) return undefined
      return getPriorityColorLight(selectedTicket.value?.priority)
    case 'status':
      if (!selectedTicket.value?.status) return undefined
      return getStatusColorLight(selectedTicket.value?.status)
    default:
      return undefined
  }
}
async function setBadgeType(typeId: string) {
  const fullType = await getTicketType(typeId)
  if (fullType) badgeTypeText.value = fullType.name[locale.value]
}

watch(
  selectedTicket,
  async (newValue) => {
    if (newValue) {
      if (newValue.typeId?.length) setBadgeType(newValue.typeId)
      // ticketParent.value =
    }
  },
  { immediate: true }
)
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.edit-ticket-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &__badges {
    display: flex;
    flex-direction: row;
    gap: 8px;
    margin-bottom: 16px;
  }
  &__navigation {
    align-items: center;
    display: flex;
    height: 64px;
    justify-content: space-between;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      flex-direction: column;
      margin-bottom: 48px;
      align-items: flex-start;
    }

    p {
      @include font-mixins.desktop-bold-paragraph;
      color: #000;
    }
  }
}
</style>
